import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  TextField,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TWButton, PageLayout, FileUploadZone, type FileUploadZoneRef } from '../../../shared/components';
import { useMe } from '../../../shared/hooks/useMe';
import { canWriteQuestionnaireFlow } from '../../../shared/permissions/capabilities';
import { QUEST_ACCESS } from '../../../shared/permissions/slugs';
import { postExtractQuestions, type ExtractQuestionsApiResponse } from '../utils/questionnaireApi';
import { QuestionEnglishInline } from './QuestionEnglishInline';

interface ExtractedQuestion {
  sheet_name?: string;
  row_index?: number;
  question_id: string;
  question_number: string;
  question_text: string;
  question_locale?: string | null;
}

// Helper function to group questions by sheet name
const groupQuestionsBySheet = (questions: ExtractedQuestion[]): Map<string, ExtractedQuestion[]> => {
  const grouped = new Map<string, ExtractedQuestion[]>();
  questions.forEach(q => {
    const sheetName = q.sheet_name || 'Questions';
    if (!grouped.has(sheetName)) {
      grouped.set(sheetName, []);
    }
    grouped.get(sheetName)!.push(q);
  });
  return grouped;
};

type ExtractResponse = ExtractQuestionsApiResponse;

const ExtractQuestions: React.FC = () => {
  const navigate = useNavigate();
  const { me } = useMe();
  const canWriteQuest = canWriteQuestionnaireFlow(me);
  const uploadZoneRef = useRef<FileUploadZoneRef>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ExtractResponse | null>(null);

  // State for editable questions
  const [editableQuestions, setEditableQuestions] = useState<ExtractedQuestion[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  const handleFileSelect = useCallback((file: File | null) => {
    setSelectedFile(file);
    setError(null);
    setResult(null);
  }, []);

  const handleExtractQuestions = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const data = await postExtractQuestions(formData, {
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(progress);
        },
      });

      setResult(data);
      // Initialize editable questions from the response
      setEditableQuestions(data.questions);
    } catch (err: any) {
      console.error('Error extracting questions:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(`Failed to extract questions: ${err.message}`);
      } else {
        setError('Failed to extract questions. Please try again.');
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setResult(null);
    setError(null);
    setEditableQuestions([]);
    setEditingIndex(null);
    setEditValue('');
    uploadZoneRef.current?.clearInput();
  };

  // Start editing a question
  const handleStartEdit = (index: number) => {
    setEditingIndex(index);
    setEditValue(editableQuestions[index].question_text);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  // Save edited question
  const handleSaveEdit = (index: number) => {
    if (editValue.trim()) {
      setEditableQuestions(prev =>
        prev.map((q, i) =>
          i === index
            ? { ...q, question_text: editValue.trim(), question_locale: undefined }
            : q
        )
      );
    }
    setEditingIndex(null);
    setEditValue('');
  };

  // Delete a question
  const handleDeleteQuestion = (index: number) => {
    setEditableQuestions(prev => {
      const newQuestions = prev.filter((_, i) => i !== index);
      // Old re-ordering code - commented out to preserve original question numbers
      // return newQuestions.map((q, i) => ({
      //   ...q,
      //   question_number: String(i + 1)
      // }));
      return newQuestions;
    });
    // If currently editing the deleted question, cancel edit
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditValue('');
    } else if (editingIndex !== null && editingIndex > index) {
      // Adjust editing index if a question before it was deleted
      setEditingIndex(editingIndex - 1);
    }
  };

  // Navigate to /questionnaire/questions with structured questions (for file upload flow)
  const handleGetAnswers = () => {
    // Pass structured questions to preserve sheet_name/row_index for download feature
    const structuredQuestions = editableQuestions.map(q => ({
      sheet_name: q.sheet_name || null,
      row_index: q.row_index || null,
      question_id: q.question_id,
      question_number: q.question_number,
      question_text: q.question_text,
      question_locale: q.question_locale ?? null,
    }));
    
    // Pass structured questions and file_uuid to questionnaire/questions
    navigate('/questionnaire/questions', { 
      state: { 
        structuredQuestions,
        file_uuid: result?.file_uuid
      } 
    });
  };

  // Custom right actions for the header
  const headerRightActions = (
    <Button
      variant="text"
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate('/questionnaire/questions')}
      sx={{
        color: '#333',
        textTransform: 'none',
        fontSize: '0.9rem',
        fontWeight: 500,
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
      }}
    >
      Ask questions
    </Button>
  );

  return (
    <PageLayout rightActions={headerRightActions}>
      {/* Title and Instructions */}
      <Box sx={{ mb: '24px', textAlign: 'left' }}>
        <Typography variant="h1" sx={{ 
          fontWeight: 700,
          fontSize: '2rem',
          color: '#333',
          mb: '12px',
          lineHeight: 1.2
        }}>
          Extract questions from document
        </Typography>
        <Typography variant="body1" sx={{ 
          color: '#666',
          fontSize: '1rem',
          mb: 0,
          lineHeight: 1.5
        }}>
          Upload a CSV or document file containing security questions. OneSec will extract and parse the questions for you.
        </Typography>
      </Box>

      {!canWriteQuest && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You do not have permission to extract questions. Requires questionnaire access ({QUEST_ACCESS}) — backend route questionnaire.extract_questions.
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontSize: '0.95rem'
            }
          }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Upload Section */}
      {!result && (
        <Paper
          elevation={2}
          sx={{
            p: 4,
            mb: 3,
            borderRadius: 2,
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <FileUploadZone
            ref={uploadZoneRef}
            accept=".csv,.xlsx,.xls"
            supportedFormatsText="Supported formats: CSV, Excel (.csv, .xlsx, .xls)"
            file={selectedFile}
            onFileSelect={handleFileSelect}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
          />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mt: 4,
            }}
          >
            <TWButton
              label={isUploading ? 'Extracting...' : 'Extract Questions'}
              onClick={handleExtractQuestions}
              loading={isUploading}
              disabled={!canWriteQuest || !selectedFile || isUploading}
              startIcon={!isUploading ? <CloudUploadIcon /> : undefined}
              sx={{
                fontSize: '1rem',
                minWidth: 220,
                backgroundColor: '#003D4F',
                '&:hover': {
                  backgroundColor: '#156C86',
                },
              }}
            />
            {selectedFile && (
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={isUploading}
                sx={{
                  minHeight: '48px',
                  minWidth: 120,
                  border: '1.5px solid #003D4F',
                  color: '#003D4F',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  borderRadius: 0,
                  '&:hover': {
                    border: '1.5px solid #003D4F',
                    backgroundColor: '#F5F5F5',
                  },
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* Results Section */}
      {result && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: '50px',
            borderRadius: '4px',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            border: '1px solid #e0e0e0'
          }}
        >
          {/* Questions Grouped by Sheet */}
          {editableQuestions.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {Array.from(groupQuestionsBySheet(editableQuestions)).map(([sheetName, questions], accordionIdx) => {
                const isLastAccordion = accordionIdx === Array.from(groupQuestionsBySheet(editableQuestions)).length - 1;
                return (
                <Accordion 
                  key={sheetName} 
                  defaultExpanded={sheetName === 'Information Security' || false}
                  sx={{
                    border: 'none',
                    borderRadius: '0 !important',
                    '&:before': { display: 'none' },
                    boxShadow: 'none',
                    '&.Mui-expanded': {
                      margin: 0,
                    },
                    mt: accordionIdx === 0 ? 0 : 0,
                    pb: isLastAccordion ? 0 : 0,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#333', fontSize: '1.25rem' }} />}
                    sx={{
                      backgroundColor: 'transparent',
                      borderRadius: 0,
                      px: 0,
                      py: '12px',
                      minHeight: '50px',
                      borderBottom: '1px solid #e0e0e0',
                      '&.Mui-expanded': {
                        minHeight: '50px',
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '8px 0',
                        '&.Mui-expanded': {
                          margin: '8px 0',
                        },
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '1rem', lineHeight: 1.5, margin: 0 }}>
                        {sheetName}
                      </Typography>
                      <Typography sx={{ color: '#666', fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5, margin: 0 }}>
                        {questions.length} Questions
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, pt: 0, pb: 0, px: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {questions.map((q, idx) => {
                        const globalIndex = editableQuestions.findIndex(
                          eq => eq.question_number === q.question_number && 
                                eq.question_text === q.question_text &&
                                eq.sheet_name === q.sheet_name
                        );
                        return (
                          <Box
                            key={globalIndex}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              py: '17.5px',
                              px: 0,
                              gap: 2,
                              borderBottom: '1px solid #e0e0e0',
                            }}
                          >
                            {/* Question Number */}
                            <Box sx={{ 
                              width: '57px',
                              display: 'flex',
                              alignItems: 'flex-start',
                              pt: 0,
                              flexShrink: 0
                            }}>
                              <Typography 
                                sx={{ 
                                  color: '#333', 
                                  fontSize: '0.95rem', 
                                  lineHeight: 1.5,
                                  fontWeight: 400,
                                  margin: 0,
                                  padding: 0
                                }}
                              >
                                {q.question_number}
                              </Typography>
                            </Box>
                            
                            {/* Question Text */}
                            <Box sx={{ flex: 1, pr: 1.5, minWidth: 0 }}>
                              {editingIndex === globalIndex ? (
                                <TextField
                                  fullWidth
                                  multiline
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  autoFocus
                                  size="small"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '0.95rem',
                                      lineHeight: 1.5,
                                      '& fieldset': {
                                        borderColor: '#e0e0e0',
                                      },
                                      '&:hover fieldset': {
                                        borderColor: '#48a1ad',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#48a1ad',
                                      },
                                    },
                                  }}
                                />
                              ) : (
                                <QuestionEnglishInline
                                  questionLocale={q.question_locale}
                                  accent="legacy"
                                  originalNode={
                                    <Typography
                                      sx={{
                                        color: '#333',
                                        fontSize: '0.95rem',
                                        lineHeight: 1.5,
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        display: 'block',
                                        margin: 0,
                                        padding: 0,
                                      }}
                                    >
                                      {q.question_text}
                                    </Typography>
                                  }
                                />
                              )}
                            </Box>
                            
                            {/* Action Icons */}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, pr: 0 }}>
                              {editingIndex === globalIndex ? (
                                <>
                                  <Tooltip title="Save">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleSaveEdit(globalIndex)}
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        color: '#48a1ad',
                                        padding: '6px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        '&:hover': { 
                                          backgroundColor: 'rgba(72, 161, 173, 0.1)',
                                        },
                                      }}
                                    >
                                      <CheckIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cancel">
                                    <IconButton
                                      size="small"
                                      onClick={handleCancelEdit}
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        color: '#999',
                                        padding: '6px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        '&:hover': { 
                                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                      }}
                                    >
                                      <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : (
                                <>
                                  <Tooltip title="Edit question">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleStartEdit(globalIndex)}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        color: '#888',
                                        padding: '6px',
                                        backgroundColor: 'transparent',
                                        border: '1.5px solid #d0d0d0',
                                        borderRadius: '50%',
                                        minWidth: 32,
                                        '&:hover': { 
                                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                          borderColor: '#999',
                                          color: '#666',
                                        },
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete question">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteQuestion(globalIndex)}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        color: '#888',
                                        padding: '6px',
                                        backgroundColor: 'transparent',
                                        border: '1.5px solid #d0d0d0',
                                        borderRadius: '50%',
                                        minWidth: 32,
                                        '&:hover': { 
                                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                          borderColor: '#999',
                                          color: '#666',
                                        },
                                      }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" sx={{ color: '#666' }}>
                No questions remaining. Upload another file or go back to home.
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 2, 
            mt: '50px', 
            pt: 0, 
          }}>
            {editableQuestions.length > 0 && (
              <TWButton
                label="Get answers"
                onClick={handleGetAnswers}
                sx={{
                  fontSize: '1rem',
                  minWidth: 180,
                  minHeight: '48px',
                  backgroundColor: '#48a1ad',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: '#3a8a94',
                  },
                }}
              />
            )}
            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{
                minHeight: '48px',
                minWidth: 180,
                border: '1.5px solid #003D4F',
                color: '#003D4F',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                borderRadius: 0,
                '&:hover': {
                  border: '1.5px solid #003D4F',
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              Upload another file
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              sx={{
                minHeight: '48px',
                minWidth: 150,
                border: '1.5px solid #003D4F',
                color: '#003D4F',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                borderRadius: 0,
                '&:hover': {
                  border: '1.5px solid #003D4F',
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              Back to home
            </Button>
          </Box>
        </Paper>
      )}
    </PageLayout>
  );
};

export default ExtractQuestions;
